import { useState, useCallback } from 'react'
import api from '@/admin/lib/api'
import { toast } from 'sonner'
import type { DataTableColumn } from '@/admin/components/data-table'
import { visibleOrderedColumns } from '@/admin/components/data-table/visible-columns'

import { toastApiError } from '@/shared/lib/api-error'
import {
  exportScopeToParams,
  type ExportScope,
} from '@/admin/lib/export-scope'

interface UseTableExportOptions<T> {
  endpoint: string
  columns: DataTableColumn<T>[]
  columnVisibility: Record<string, boolean>
  /** The table's column order, so the file matches the screen. */
  columnOrder?: string[]
  /**
   * Export the columns on screen, in order, under their headers. Needs an
   * endpoint that reads ``export_columns``; otherwise it exports every field.
   */
  followVisibleColumns?: boolean
  params: Record<string, string>
}

export function useTableExport<T>({
  endpoint,
  columns,
  columnVisibility,
  columnOrder,
  followVisibleColumns = false,
  params,
}: UseTableExportOptions<T>) {
  const [isExporting, setIsExporting] = useState(false)

  const exportCsv = useCallback(async (scope: ExportScope = 'current') => {
    setIsExporting(true)
    try {
      // The scope decides the time window: "current" uses the on-screen filters
      // as-is; a period/all scope owns the time dimension (see exportScopeToParams).
      const exportParams: Record<string, string> = {
        ...exportScopeToParams(scope, params),
        download: 'csv',
      }
      if (followVisibleColumns) {
        exportParams.export_columns = JSON.stringify(
          visibleOrderedColumns(columns, columnVisibility, columnOrder).map((col) => ({
            id: col.id,
            // Headers are typed as strings; guard the JSON boundary anyway.
            label: typeof col.header === 'string' && col.header ? col.header : col.id,
          })),
        )
      }
      const response = await api.get(endpoint, {
        params: exportParams,
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      // Extract filename from Content-Disposition header if available
      const disposition = response.headers['content-disposition']
      let filename = 'export.csv'
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/)
        if (match?.[1]) filename = match[1]
      }

      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Export downloaded')
    } catch (err) {
      toastApiError(err, 'Export failed');
    } finally {
      setIsExporting(false)
    }
  }, [endpoint, params, followVisibleColumns, columns, columnVisibility, columnOrder])

  return { exportCsv, isExporting }
}
